import {
  createInspectionRequest,
  deleteInspectionRequest,
  editInspectionRequest,
  fetchCertainInspectionRequest,
  fetchInspectionsRequest,
  editNameInspectionRequest,
} from 'api';
import { fetchPendingInspectionsRequest } from 'api/inspectionsApi/inspectionsApi';
import { EditInspectionRequestType } from 'api/inspectionsApi/types';
import { PaginationType } from 'common';
import { InspectionStatus, Pagination } from 'enums';
import {
  setAuthLoading,
  setCertainInspection,
  setInspections,
  setInspectionsAfterScroll,
  setInspectionsPagination,
  setNotification,
  setInspectionsLoadingStatus,
} from 'state/actions';
import { setFullLoading } from 'state/actions/auth/authActions';
import { AppRootStateType } from 'state/reducers';
import { TypedDispatch } from 'state/types';

export const fetchInspections =
  (isScroll: boolean, propertyId?: string) =>
  async (dispatch: TypedDispatch, getState: () => AppRootStateType) => {
    const { page, search } = {
      page: isScroll ? getState().inspections.page : Pagination.START_PAGE,
      search: getState().inspections.search.trim(),
    };

    try {
      dispatch(setInspectionsLoadingStatus(true));
      const response = await fetchInspectionsRequest({
        page,
        q: search || undefined,
        take: Pagination.TAKE,
        propertyId,
        creationStatus: InspectionStatus.CREATED,
      });
      const pagination: PaginationType = {
        page: response?.data.page,
        count: response?.data.count,
      };

      dispatch(setInspectionsPagination(pagination));
      dispatch(
        isScroll
          ? setInspectionsAfterScroll(response?.data.rows)
          : setInspections(response?.data.rows),
      );
    } finally {
      dispatch(setInspectionsLoadingStatus(false));
    }
  };

export const fetchPendingInspections = () => async (dispatch: TypedDispatch) => {
  try {
    dispatch(setAuthLoading(true));

    return await fetchPendingInspectionsRequest();
  } finally {
    dispatch(setAuthLoading(false));
  }
};

export const createInspection = () => async (dispatch: TypedDispatch) => {
  try {
    dispatch(setAuthLoading(true));

    return await createInspectionRequest();
  } finally {
    dispatch(setAuthLoading(false));
  }
};

export const editInspection =
  (inspection: EditInspectionRequestType & { id: string; fullReprocess: boolean }) =>
  async (dispatch: TypedDispatch) => {
    try {
      dispatch(setAuthLoading(true));
      const response = await editInspectionRequest(inspection);

      return response?.data;
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const editNameInspection =
  ({ name, id }: { name: string; id: string }) =>
  async (dispatch: TypedDispatch) => {
    try {
      dispatch(setAuthLoading(true));
      const response = await editNameInspectionRequest({ name, id });

      return response?.status;
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const deleteInspection =
  (id: string, confirm: boolean) => async (dispatch: TypedDispatch) => {
    try {
      dispatch(setAuthLoading(true));
      const response = await deleteInspectionRequest(id, confirm);

      dispatch(setNotification(response?.data?.message));

      return response;
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const fetchCertainInspection =
  (id: string, fullPageLoad?: boolean, loadFoundProperties?: boolean, silent?: boolean) =>
  async (dispatch: TypedDispatch) => {
    try {
      if (fullPageLoad) {
        dispatch(setFullLoading(true));
      } else {
        dispatch(setAuthLoading(true));
      }
      const response = await fetchCertainInspectionRequest(
        id,
        loadFoundProperties,
        silent,
      );

      // Don't clobber already-loaded inspection with an empty/failed response
      // (a failed background refresh resolves undefined via the interceptor).
      if (response?.data) {
        dispatch(setCertainInspection(response.data));
      }

      return response;
    } finally {
      if (fullPageLoad) {
        dispatch(setFullLoading(false));
      } else {
        dispatch(setAuthLoading(false));
      }
    }
  };
